import { NetworkProfile } from '@/services/networks/networksApi'
import {
  DEFAULT_RATE_LIMIT_TYPES,
  RATE_LIMIT_TYPES,
  type RateLimitScope,
} from '@/services/earnings/rateLimits'

/** Objective types a line item can carry, in display order. */
export const OBJECTIVE_TYPES = [
  'IMPRESSION',
  'RENDERED_IMPRESSION',
  'VISIBLE_IMPRESSION',
  'VIEWABLE_IMPRESSION',
  'CLICK',
  'CONVERSION',
  'BUDGET',
] as const
export type ObjectiveType = (typeof OBJECTIVE_TYPES)[number]

/** Bid specification fields. Marketplace line items never offer CPA. */
export const BID_FIELDS = ['cpm', 'cpc', 'cpa'] as const
export type BidField = (typeof BID_FIELDS)[number]

export const MARKETPLACE_BID_FIELDS: BidField[] = ['cpm', 'cpc']

/** The network profile stores UI defaults as a JSON blob. */
export function parseAdnDefaults(profile?: { adnDefaults?: string }): Record<string, unknown> {
  try {
    return JSON.parse(profile?.adnDefaults ?? '{}')
  } catch {
    return {}
  }
}

/** `{ IMPRESSION: true, CLICK: false }` → `['IMPRESSION']`. */
export function enabledFields(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return []
  return Object.entries(value as Record<string, boolean>)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key)
}

/** Which of the three names the network calls a rate limit by. */
export type CappingAlias = '1' | '2' | '3'

export interface LineItemFieldSettings {
  objectives: ObjectiveType[]
  bidFields: BidField[]
  /** Fields a line item may be capped on. */
  cappingTypes: ObjectiveType[]
  /** Scope a newly added cap starts on. */
  cappingScope: RateLimitScope
  /** Whether the network calls them rate limits, caps or frequency caps. */
  cappingAlias: CappingAlias
  /** Custom events a dynamic bid strategy may optimise towards, by ID. */
  biddingCustomEvents: string[]
  /** Whether orders carry a budget (and smoothing) — off unless the network turns it on. */
  orderObjectives: boolean
  /** What the network calls a supplementary fee — "Team Custom Fee", say. */
  supplementaryFeeLabel?: string
}

/** The custom events the network allows dynamic bidding on — `adnDefaults.dynamicBiddingCustomEvents`. */
export function biddingCustomEventIds(defaults: Record<string, unknown>): string[] {
  const value = defaults.dynamicBiddingCustomEvents
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string' && !!id) : []
}

/**
 * Which objective and bid fields a line item may show, per network settings.
 * Marketplace line items read the `marketplace*` variants. An unset (or empty)
 * setting means the network has not restricted anything, so everything shows.
 */
export function lineItemFieldSettings(
  profile: NetworkProfile | undefined,
  isMarketplace: boolean
): LineItemFieldSettings {
  const defaults = parseAdnDefaults(profile?.profile)
  const allowedBids = isMarketplace ? MARKETPLACE_BID_FIELDS : [...BID_FIELDS]

  const objectives = enabledFields(
    defaults[isMarketplace ? 'marketplaceObjectiveFields' : 'objectiveFields']
  ).filter((f): f is ObjectiveType => OBJECTIVE_TYPES.includes(f as ObjectiveType))

  const bidFields = enabledFields(
    defaults[isMarketplace ? 'marketplaceBidSpecificationFields' : 'bidSpecificationFields']
  ).filter((f): f is BidField => allowedBids.includes(f as BidField))

  const cappingTypes = enabledFields(defaults.rateLimitFields).filter((f): f is ObjectiveType =>
    RATE_LIMIT_TYPES.includes(f as ObjectiveType)
  )

  // Only PER_USER and GLOBAL exist as scopes; the network setting also allows
  // PER_DEVICE, which the API treats as per user.
  const cappingScope: RateLimitScope = defaults.rateLimitScope === 'GLOBAL' ? 'GLOBAL' : 'PER_USER'

  const alias = String(defaults.rateLimitTranslations ?? '1')

  const translations = defaults.translations as Record<string, unknown> | undefined
  const supplementaryFeeLabel = translations?.supplementaryFee

  return {
    objectives: objectives.length ? objectives : [...OBJECTIVE_TYPES],
    bidFields: bidFields.length ? bidFields : allowedBids,
    cappingTypes: cappingTypes.length ? cappingTypes : DEFAULT_RATE_LIMIT_TYPES,
    cappingScope,
    cappingAlias: alias === '2' || alias === '3' ? alias : '1',
    biddingCustomEvents: biddingCustomEventIds(defaults),
    orderObjectives: defaults.orderObjectives === true,
    supplementaryFeeLabel:
      typeof supplementaryFeeLabel === 'string' && supplementaryFeeLabel.trim()
        ? supplementaryFeeLabel
        : undefined,
  }
}
